import { homApi } from '~/redux/apis/hom'
import { RegisterFields } from '~/types/sign-up'

import { callApi } from '~/services/axiosInstance.service'

import { AuthenticationResponse, Result, UserExistResponse } from '~/redux/apis/types'

import { AuthTypes } from '../types/auth.types'

interface IUserExistResponse {
  type: string
  details: {
    userDoesExist: boolean
    userHasPassword: boolean
    simpleUserProfile: {
      avatar: string
      nickname: string
    }
    type: AuthTypes
  }
}

const authApi = homApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    forgotPassword: build.mutation<{ success: boolean; message: string }, { email: string }>({
      query: (body) => ({
        url: '/v1/auth/forgot-password',
        method: 'POST',
        body,
      }),
    }),

    resetPassword: build.mutation<{ success: boolean; message: string }, { password: string; key: string }>({
      query: (body) => ({
        url: '/v1/auth/reset-password',
        method: 'POST',
        body,
      }),
    }),

    changePassword: build.mutation<
      { type: 'error' | 'success'; message: string },
      { password: string; currentPassword: string }
    >({
      query: (body) => ({
        url: '/v1/auth/change-password',
        method: 'PATCH',
        body,
      }),
    }),

    loginUserWithEmailPassword: build.mutation<
      Result<AuthenticationResponse>,
      { email: string; password: string; pseudoId: string | null }
    >({
      queryFn: (data) => {
        return callApi<Result<AuthenticationResponse>>({
          url: '/v1/auth/login',
          method: 'POST',
          data,
        })
      },
    }),

    loginWithFacebook: build.mutation<
      Result<(AuthenticationResponse & { created: boolean }) | UserExistResponse>,
      { code: string; redirectUri: string; pseudoId: string | null }
    >({
      queryFn: (data) => {
        return callApi<Result<AuthenticationResponse & { created: boolean }>>({
          url: '/v1/auth/facebook-callback',
          method: 'POST',
          data,
        })
      },
    }),

    loginWithGoogle: build.mutation<
      { type: 'success' | 'error'; redirectUrl: string },
      {
        from: string | undefined
        isMobile: boolean
        recaptchaToken?: string
        captchaAnswer?: string
        pseudoId: string | null
      }
    >({
      queryFn: (data) => {
        return callApi<{ type: 'success' | 'error'; redirectUrl: string }>({
          url: '/v1/auth/google',
          method: 'POST',
          data,
        })
      },
    }),

    googleCallBack: build.mutation<
      Result<(AuthenticationResponse & { created: boolean }) | UserExistResponse>,
      { code: string }
    >({
      queryFn: (data) => {
        return callApi<Result<AuthenticationResponse & { created: boolean }>>({
          url: '/v1/auth/google-callback',
          method: 'POST',
          data,
        })
      },
    }),

    checkIfUserInDb: build.mutation<IUserExistResponse, { email: string }>({
      query: (body) => ({
        url: '/v1/auth/user-exist',
        method: 'post',
        body,
      }),
    }),

    registerUser: build.mutation<
      Result<AuthenticationResponse>,
      RegisterFields & { recaptchaToken: string; captchaAnswer?: string; pseudoId: string | null }
    >({
      queryFn: (data) => {
        return callApi<Result<AuthenticationResponse>>({
          url: '/v1/auth/register',
          method: 'POST',
          data,
        })
      },
    }),

    registerCampaignUser: build.mutation<
      Result<AuthenticationResponse>,
      { email: string; password: string; campaign: string; recaptchaToken: string; captchaAnswer?: string }
    >({
      queryFn: (data) => {
        return callApi<Result<AuthenticationResponse>>({
          url: '/v1/free-access/register',
          method: 'POST',
          data,
        })
      },
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useChangePasswordMutation,
  useCheckIfUserInDbMutation,
  useForgotPasswordMutation,
  useLoginUserWithEmailPasswordMutation,
  useLoginWithFacebookMutation,
  useRegisterUserMutation,
  useRegisterCampaignUserMutation,
  useResetPasswordMutation,
  useLoginWithGoogleMutation,
  useGoogleCallBackMutation,
} = authApi
